import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import jwt from "jsonwebtoken"
import { Document, Block, Text } from "@contentful/rich-text-types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Generates a JWT token with a 1-hour expiry
 * @param payload - Object containing the data to embed in the token (e.g., user ID, role)
 * @returns Signed JWT token string
 */
export function generateJwtToken(): string {
  // Sign the token with the provided payload, secret key, and options
  const payload = {
    iat: Math.floor(Date.now() / 1000), // Issued at time
  }
  const token = jwt.sign(payload, process.env.NEXT_PUBLIC_JWT_TOKEN, {
    expiresIn: "1h", // Token expires in 1 hour
    algorithm: "HS256", // Default algorithm
  })

  return token
}

/**
 * Recursively extracts all text nodes from a Contentful rich text document.
 */
const extractTextFromDocument = (node: Block | Text | undefined): string => {
  if (!node) {
    return ""
  }

  if (node.nodeType === "text") {
    return node.value
  }

  if ("content" in node && Array.isArray(node.content)) {
    return node.content.map(extractTextFromDocument).join(" ")
  }
  return ""
}

/**
 * Calculates estimated reading time based on text length and words per minute (WPM).
 *
 * @param text - The input text content to analyze.
 * @param wpm - Average reading speed (words per minute). Defaults to 200 WPM.
 * @returns Estimated read time in minutes, rounded up to the nearest whole number.
 */
export const calculateReadTime = (
  text: Document | undefined,
  image: string | null,
  wpm: number = 265, //as per medium.com
): string => {
  const wordCount = extractTextFromDocument(text).trim().split(/\s+/).length
  const imageReadTime = image?.startsWith("https") ? 0.2 : 0 //If we have image, add 12 seconds (0.2 min)
  const totalMinutes = wordCount / wpm + imageReadTime

  const rounded = Math.max(1, Math.round(totalMinutes)) // ensures minimum is 1
  return `${rounded} min read`
}

// Convert the Input date string into a JavaScript Date object
// @param isoDate - The date string in ISO format (e.g., "2025-05-14")
// and format it as "Mon dd, yyyy" (e.g., "May 14, 2025")
//
export const formatDate = (isoDate: string): string => {
  try {
    const [year, month, day] = isoDate?.split("-").map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    })
  } catch {
    return ""
  }
}

type Dimensions = { width: number; height: number }

export const getImageDimensions = (url: string): Promise<Dimensions> => {
  // Only run in browser environment
  if (typeof window === "undefined") {
    return Promise.resolve({ width: 0, height: 0 })
  }

  return new Promise((resolve) => {
    const img = new Image()
    img.src = url

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }

    img.onerror = () => {
      resolve({ width: 0, height: 0 })
    }
  })
}

export const getInitials = (name: string) => {
  if (!name) return ""
  const words = name.trim().split(/\s+/) // Split by whitespace
  if (words.length === 1) {
    return words[0][0].toUpperCase()
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}
